import { FileText } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CountrySelector } from '@/components/ui/country-selector';
import { getLegalStructuresForCountry } from '@/constants/legalStructures';
import type { FundFormData } from './types';

interface LegalStructureSectionProps {
  formData: FundFormData;
  errors: Record<string, string>;
  onInputChange: (field: keyof FundFormData, value: string) => void;
}

export function LegalStructureSection({
  formData,
  errors,
  onInputChange,
}: LegalStructureSectionProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-slate-700" />
          <CardTitle>Legal Structure</CardTitle>
        </div>
        <CardDescription>
          Legal entity structure and regulatory information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="domicile_country">Domicile Country *</Label>
            <CountrySelector
              value={formData.domicile_country}
              onValueChange={(value) =>
                onInputChange('domicile_country', value)
              }
              placeholder="Select a country"
            />
          </div>
          <div>
            <Label htmlFor="legal_structure">Legal Structure *</Label>
            <Select
              value={formData.legal_structure}
              onValueChange={(value) => onInputChange('legal_structure', value)}
            >
              <SelectTrigger
                id="legal_structure"
                className={errors.legal_structure ? 'border-red-500' : ''}
              >
                <SelectValue placeholder="Select a legal structure">
                  {formData.legal_structure &&
                    getLegalStructuresForCountry(
                      formData.domicile_country
                    ).find((s) => s.value === formData.legal_structure)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {getLegalStructuresForCountry(formData.domicile_country).map(
                  (structure) => (
                    <SelectItem key={structure.value} value={structure.value}>
                      <span>
                        <span className="block">{structure.label}</span>
                        {structure.description && (
                          <span className="block text-xs text-muted-foreground">
                            {structure.description}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
            {errors.legal_structure && (
              <p className="text-sm text-red-600 mt-1">
                {errors.legal_structure}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="regulatory_regime">Regulatory Regime</Label>
            <Select
              value={formData.regulatory_regime}
              onValueChange={(value) =>
                onInputChange('regulatory_regime', value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aifmd">
                  AIFMD (Alternative Investment Fund Managers Directive)
                </SelectItem>
                <SelectItem value="ucits">
                  UCITS (Undertakings for Collective Investment)
                </SelectItem>
                <SelectItem value="eltif">
                  ELTIF (European Long-term Investment Funds)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="status">Fund Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => onInputChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="setup">Setup</SelectItem>
                <SelectItem value="fundraising">Fundraising</SelectItem>
                <SelectItem value="investing">Investing</SelectItem>
                <SelectItem value="harvesting">Harvesting</SelectItem>
                <SelectItem value="liquidated">Liquidated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="lei_code">LEI Code</Label>
            <Input
              id="lei_code"
              value={formData.lei_code}
              onChange={(e) => onInputChange('lei_code', e.target.value)}
              placeholder="Legal Entity Identifier"
            />
          </div>
          <div>
            <Label htmlFor="tax_id">Tax ID</Label>
            <Input
              id="tax_id"
              value={formData.tax_id}
              onChange={(e) => onInputChange('tax_id', e.target.value)}
              placeholder="Tax identification number"
            />
          </div>
          <div>
            <Label htmlFor="coc_number">CoC Number</Label>
            <Input
              id="coc_number"
              value={formData.coc_number}
              onChange={(e) => onInputChange('coc_number', e.target.value)}
              placeholder="Chamber of Commerce (KvK) number"
              maxLength={20}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
